import React from 'react';
import { connect } from 'react-redux';

import {
  Avatar,
  IconSocialMediaFacebook,
  IconSocialMediaInstagram,
  IconSocialMediaTwitter,
  InlineTextButton,
  ListingCard,
  Logo,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
  Page,
} from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { logout } from '../../ducks/auth.duck';
import { getCurrentUserTypeRoles } from '../../util/userHelpers';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import heroImage from '../../assets/opposhop-landing-hero.jpg';

import css from './LandingPage.module.css';

const stats = [
  { icon: 'users', label: 'Buyer-led requests' },
  { icon: 'listing', label: 'Supplier-ready opportunities' },
  { icon: 'speed', label: 'Simple direct conversations' },
];

const conceptCards = [
  {
    icon: 'bag',
    number: '01',
    title: 'Do you want something?',
    text:
      'List whatever you are looking for. Be as vague or as descriptive as you want. Start a conversation about your needs with potential suppliers.',
    cta: 'Post a new listing',
    routeName: 'NewListingPage',
  },
  {
    icon: 'search',
    number: '02',
    title: 'Check what others have posted',
    text:
      'Browse requests posted by other people. They may be looking for something you can supply. Every listing is an opportunity for suppliers to connect with real customer demand.',
    cta: 'Search listings',
    routeName: 'SearchPage',
  },
  {
    icon: 'help',
    number: '03',
    title: 'Still not quite sure how it works?',
    text: 'Learn how Opposhop helps buyers and suppliers connect through request-based shopping.',
    cta: 'About us',
    href: '#concept',
  },
];

const processSteps = [
  {
    icon: 'document',
    number: '01',
    title: 'Post what you need',
    text:
      'Create a request for the item, product, or service you are looking for. Add as much or as little detail as you want.',
  },
  {
    icon: 'discover',
    number: '02',
    title: 'Suppliers discover your request',
    text:
      'Relevant sellers can browse requests and reach out with offers. Your listing does the work \u2014 no cold searching required.',
  },
  {
    icon: 'connect',
    number: '03',
    title: 'Connect and complete',
    text:
      'Discuss details, compare options, and move forward with the right supplier. Simple, direct, and buyer-led.',
  },
];

const footerLinks = [
  { label: 'About us', href: '#concept' },
  { label: 'Search listings', routeName: 'SearchPage' },
  { label: 'Post a new listing', routeName: 'NewListingPage' },
  { label: 'Terms of Service', routeName: 'TermsOfServicePage' },
  { label: 'Privacy Policy', routeName: 'PrivacyPolicyPage' },
];

const Icon = props => {
  const { name, className } = props;

  switch (name) {
    case 'arrow':
      return (
        <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M3.5 8h8" />
          <path d="M8.5 4.5 12 8l-3.5 3.5" />
        </svg>
      );
    case 'plus':
      return (
        <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M8 3.5v9" />
          <path d="M3.5 8h9" />
        </svg>
      );
    case 'users':
      return (
        <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M6.6 7.2a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8Z" />
          <path d="M1.8 13.4c.4-2.4 2.2-3.8 4.8-3.8s4.4 1.4 4.8 3.8" />
          <path d="M10.9 4.1a2 2 0 0 1 0 3.8" />
          <path d="M12.3 9.9c1.2.6 1.9 1.7 2.1 3.5" />
        </svg>
      );
    case 'listing':
      return (
        <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M3.2 2.4h9.6v11.2H3.2z" />
          <path d="M5.4 5.2h5.2" />
          <path d="M5.4 8h5.2" />
          <path d="M5.4 10.8h3.2" />
        </svg>
      );
    case 'speed':
      return (
        <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M2.4 8.7a5.6 5.6 0 1 1 11.2 0" />
          <path d="M8 8.7 11 5.8" />
          <path d="M5.2 12.4h5.6" />
        </svg>
      );
    case 'bag':
      return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M6.6 8.3h10.8l-.8 10.4H7.4L6.6 8.3Z" />
          <path d="M9.2 8.3V6.8a2.8 2.8 0 0 1 5.6 0v1.5" />
          <path d="M9.3 12h5.4" />
        </svg>
      );
    case 'search':
      return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="m16.8 16.8 3.4 3.4" />
          <circle cx="11" cy="11" r="6.2" />
        </svg>
      );
    case 'help':
      return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="8.8" />
          <path d="M9.4 9.2a2.7 2.7 0 0 1 5.2 1c0 1.9-2 2.2-2.4 3.7" />
          <path d="M12.1 17.2h.1" />
        </svg>
      );
    case 'document':
      return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M7 3.8h7l3 3v13.4H7z" />
          <path d="M14 3.8V7h3" />
          <path d="M9.5 11h5" />
          <path d="M9.5 14.4h5" />
        </svg>
      );
    case 'discover':
      return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M3.8 12s3-5 8.2-5 8.2 5 8.2 5-3 5-8.2 5-8.2-5-8.2-5Z" />
          <circle cx="12" cy="12" r="2.7" />
        </svg>
      );
    case 'connect':
      return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M8.2 12.7 5.9 15a3.1 3.1 0 0 0 4.4 4.4l2.3-2.3" />
          <path d="m11.4 8.2 2.3-2.3A3.1 3.1 0 1 1 18.1 10l-2.3 2.3" />
          <path d="m9.4 14.6 5.2-5.2" />
        </svg>
      );
    case 'mail':
      return (
        <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M2.2 4.1h11.6v7.8H2.2z" />
          <path d="m2.6 4.5 5.4 4 5.4-4" />
        </svg>
      );
    case 'lock':
      return (
        <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M4.2 7.1h7.6v5.8H4.2z" />
          <path d="M5.6 7.1V5.5a2.4 2.4 0 0 1 4.8 0v1.6" />
        </svg>
      );
    default:
      return null;
  }
};

const RouteLink = props => {
  const { children, className, item } = props;
  return item.href ? (
    <a className={className} href={item.href}>
      {children}
    </a>
  ) : (
    <NamedLink className={className} name={item.routeName}>
      {children}
    </NamedLink>
  );
};

const CTAButtons = props => {
  const { centered = false } = props;
  return (
    <div className={centered ? css.ctaButtonsCentered : css.ctaButtons}>
      <NamedLink className={css.primaryButton} name="SearchPage">
        <span>Browse listings</span>
        <Icon name="arrow" className={css.buttonIcon} />
      </NamedLink>
      <NamedLink className={css.secondaryButton} name="NewListingPage">
        <Icon name="plus" className={css.buttonIcon} />
        <span>{centered ? 'Post a new listing' : 'Post a request'}</span>
      </NamedLink>
    </div>
  );
};

const SectionHeader = props => {
  const { eyebrow, title, children } = props;
  return (
    <div className={css.sectionHeader}>
      <p className={css.eyebrow}>{eyebrow}</p>
      <h2>{title}</h2>
      {children ? <p className={css.sectionIntro}>{children}</p> : null}
    </div>
  );
};

const ProfileMenu = props => {
  const { currentUser, onLogout } = props;

  return (
    <Menu
      className={css.profileMenu}
      ariaLabel="Account menu"
      contentPosition="left"
      preferScreenWidthOnMobile
      useArrow
    >
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.profileAvatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink className={css.profileMenuLink} name="ProfileSettingsPage">
            <span className={css.profileMenuItemBorder} />
            Profile settings
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink className={css.profileMenuLink} name="AccountSettingsPage">
            <span className={css.profileMenuItemBorder} />
            Account settings
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.profileMenuLogoutButton} onClick={onLogout}>
            <span className={css.profileMenuItemBorder} />
            Log out
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const getTopbarInboxTab = (config, currentUser, currentUserHasListings) => {
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  return !isCustomer
    ? 'sales'
    : !isProvider
    ? 'orders'
    : currentUserHasListings
    ? 'sales'
    : 'orders';
};

const NotificationDot = ({ className, notificationCount }) => {
  return notificationCount > 0 ? <span className={className} /> : null;
};

const Header = props => {
  const { currentUser, inboxTab, isAuthenticated, notificationCount = 0, onLogout } = props;
  const authLinksMaybe = isAuthenticated ? null : (
    <>
      <span className={css.navDivider} />
      <NamedLink className={css.navLink} name="LoginPage">
        Log in
      </NamedLink>
      <NamedLink className={css.signupLink} name="SignupPage">
        Sign up
      </NamedLink>
    </>
  );
  const profileMenuMaybe = isAuthenticated ? (
    <ProfileMenu currentUser={currentUser} onLogout={onLogout} />
  ) : null;

  return (
    <header className={css.header}>
      <div className={css.headerInner}>
        <NamedLink className={css.logoLink} name="LandingPage">
          <Logo className={css.logoWrapper} logoImageClassName={css.logoImage} layout="desktop" />
        </NamedLink>
        <NamedLink className={css.searchLink} name="SearchPage">
          <Icon name="search" className={css.searchIcon} />
          <span>Search listings...</span>
        </NamedLink>
        <nav className={css.nav} aria-label="Primary">
          <NamedLink className={css.navLink} name="NewListingPage">
            Post a new listing
          </NamedLink>
          <NamedLink className={css.navLink} name="SearchPage">
            Browse listings
          </NamedLink>
          <NamedLink className={css.navLink} name="AboutUsPage">
            About Us
          </NamedLink>
          <NamedLink className={css.navLink} name="InboxPage" params={{ tab: inboxTab }}>
            <span className={css.navLinkLabel}>
              Inbox
              <NotificationDot
                className={css.notificationDot}
                notificationCount={notificationCount}
              />
            </span>
          </NamedLink>
          {authLinksMaybe}
          {profileMenuMaybe}
        </nav>
      </div>
    </header>
  );
};

const Hero = () => (
  <section className={css.hero}>
    <div className={css.heroTexture} />
    <div className={css.heroVisual} aria-hidden="true">
      <img src={heroImage} alt="" />
    </div>
    <div className={css.heroShade} />
    <div className={css.heroInner}>
      <div className={css.heroContent}>
        <div className={css.badge}>
          <span />
          REQUEST-BASED MARKETPLACE
        </div>
        <h1>OPPOSHOP</h1>
        <p className={css.heroSubheading}>Where shopping comes to you.</p>
        <p className={css.heroText}>
          Post what you need and let suppliers come to you with the right offer.
        </p>
        <CTAButtons />
        <div className={css.stats}>
          {stats.map(stat => (
            <div className={css.stat} key={stat.label}>
              <Icon name={stat.icon} className={css.statIcon} />
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Concept = () => (
  <section className={css.conceptSection} id="concept">
    <div className={css.content}>
      <SectionHeader eyebrow="THE CONCEPT" title="The Opposite of Shopping">
        On Opposhop, buyers lead and suppliers follow. Post what you need, and let the market come
        to you.
      </SectionHeader>
      <div className={css.cardGrid}>
        {conceptCards.map(card => (
          <article className={css.conceptCard} key={card.number}>
            <div className={css.cardIconWrap}>
              <Icon name={card.icon} className={css.cardIcon} />
            </div>
            <span className={css.stepPill}>{card.number}</span>
            <h3>{card.title}</h3>
            <p>{card.text}</p>
            <RouteLink item={card} className={css.cardLink}>
              <span>{card.cta}</span>
              <Icon name="arrow" className={css.inlineIcon} />
            </RouteLink>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className={css.processSection} id="how-it-works">
    <div className={css.content}>
      <SectionHeader eyebrow="PROCESS" title="How it works">
        Getting started takes minutes. Here is how Opposhop connects buyers with the right
        suppliers.
      </SectionHeader>
      <div className={css.processGrid}>
        <div className={css.processLine} />
        {processSteps.map(step => (
          <article className={css.processStep} key={step.number}>
            <div className={css.processIconWrap}>
              <Icon name={step.icon} className={css.processIcon} />
            </div>
            <span>{step.number}</span>
            <h3>{step.title}</h3>
            <p>{step.text}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const RecentListings = props => {
  const { listings = [], inProgress = false, error = null } = props;
  const recentListings = listings.slice(0, 3);
  const hasListings = recentListings.length > 0;
  const statusText = error
    ? 'Recent listings are unavailable right now.'
    : inProgress
    ? 'Loading recent listings...'
    : 'No recent listings yet.';

  return (
    <section className={css.recentListingsSection}>
      <div className={css.content}>
        <SectionHeader eyebrow="RECENT LISTINGS" title="Recently posted requests">
          See what buyers are asking for right now, then browse the full marketplace for more
          opportunities.
        </SectionHeader>

        {hasListings ? (
          <div className={css.recentListingGrid}>
            {recentListings.map(listing => (
              <ListingCard
                className={css.recentListingCard}
                key={listing.id.uuid}
                listing={listing}
                renderSizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33vw"
                variant="requestGrid"
              />
            ))}
          </div>
        ) : (
          <div className={css.recentListingEmpty}>
            <p>{statusText}</p>
          </div>
        )}

        <div className={css.recentListingsActions}>
          <NamedLink className={css.showAllButton} name="SearchPage">
            <span>Show all listings</span>
            <Icon name="arrow" className={css.buttonIcon} />
          </NamedLink>
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => (
  <section className={css.finalCta}>
    <div className={css.finalGlow} />
    <div className={css.finalContent}>
      <p className={css.eyebrow}>GET STARTED</p>
      <h2>Ready to explore what people are looking for?</h2>
      <p>
        Browse current listings or post your own request in minutes. No seller account required.
      </p>
      <CTAButtons centered />
    </div>
  </section>
);

const Footer = () => (
  <footer className={css.footer}>
    <div className={css.footerInner}>
      <div className={css.footerMain}>
        <div className={css.footerBrand}>
          <NamedLink className={css.footerLogoLink} name="LandingPage">
            <Logo className={css.logoWrapper} logoImageClassName={css.logoImage} layout="desktop" />
          </NamedLink>
          <p>
            Where shopping comes to you. A request-based marketplace connecting buyers and
            suppliers.
          </p>
          <div className={css.socialLinks}>
            <a href="https://www.facebook.com/" aria-label="Facebook">
              <IconSocialMediaFacebook />
            </a>
            <a href="https://twitter.com/" aria-label="Twitter">
              <IconSocialMediaTwitter />
            </a>
            <a href="https://www.instagram.com/" aria-label="Instagram">
              <IconSocialMediaInstagram />
            </a>
          </div>
        </div>
        <nav className={css.footerNav} aria-label="Footer navigation">
          <h2>NAVIGATION</h2>
          {footerLinks.map(item => (
            <RouteLink key={item.label} item={item} className={css.footerLink}>
              {item.label}
            </RouteLink>
          ))}
        </nav>
        <div className={css.footerAbout}>
          <h2>ABOUT</h2>
          <p>
            Opposhop flips the marketplace model. Instead of sellers listing first, buyers post
            requests and suppliers respond with offers.
          </p>
          <a className={css.emailLink} href="mailto:hello@opposhop.com">
            <Icon name="mail" className={css.emailIcon} />
            hello@opposhop.com
          </a>
        </div>
      </div>
      <div className={css.footerBottom}>
        <p>&copy; 2026 Opposhop. All rights reserved.</p>
        <span>
          <Icon name="lock" className={css.lockIcon} />
          Secure marketplace
        </span>
      </div>
    </div>
  </footer>
);

export const LandingPageComponent = props => {
  const {
    currentUser,
    currentUserHasListings,
    isAuthenticated,
    notificationCount = 0,
    onLogout,
    recentListings = [],
    recentListingsError = null,
    recentListingsInProgress = false,
  } = props;
  const config = useConfiguration();
  const inboxTab = getTopbarInboxTab(config, currentUser, currentUserHasListings);
  const schema = {
    '@context': 'http://schema.org',
    '@type': 'AboutPage',
    description:
      'Opposhop is a request-based marketplace where buyers post what they need and suppliers respond with offers.',
    name: 'About Opposhop',
  };

  return (
    <Page
      title="About Us | Opposhop"
      description="Learn how Opposhop helps buyers and suppliers connect through request-based shopping."
      schema={schema}
      rootClassName={css.page}
    >
      <TopbarContainer rootClassName={css.mobileTopbar} />
      <Header
        currentUser={currentUser}
        inboxTab={inboxTab}
        isAuthenticated={isAuthenticated}
        notificationCount={notificationCount}
        onLogout={onLogout}
      />
      <main>
        <Hero />
        <Concept />
        <HowItWorks />
        <RecentListings
          listings={recentListings}
          inProgress={recentListingsInProgress}
          error={recentListingsError}
        />
        <FinalCTA />
      </main>
      <Footer />
    </Page>
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    currentUser,
    currentUserHasListings,
    currentUserSaleNotificationCount = 0,
    currentUserOrderNotificationCount = 0,
  } = state.user;
  const {
    recentListingIds = [],
    recentListingsError = null,
    recentListingsInProgress = false,
  } = state.LandingPage || {};
  const recentListings = getListingsById(state, recentListingIds);

  return {
    currentUser,
    currentUserHasListings,
    isAuthenticated,
    notificationCount: currentUserSaleNotificationCount + currentUserOrderNotificationCount,
    recentListings,
    recentListingsError,
    recentListingsInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: () => dispatch(logout()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LandingPageComponent);
